import * as chartjs from "chart.js";
import { ChartOptions } from "chart.js";
import "chartjs-plugin-datalabels";
import { type } from "os";
import React, { useEffect, useMemo, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { EActionType, EventLog } from "../../../Model/EventLog";

const colourPallete = [
  "#418CF0",
  "#F7A246",
  "#FF0000",
  "#01FFFE",
  "#FFA6FE",
  "#FFDB66",
  "#006401",
  "#010067",
  "#95003A",
  "#007DB5",
  "#FF00F6",
  "#FFEEE8",
];

///HACK : find a way to enumerate number enum
const actionTypes = [
  //EActionType.Unknown,
  EActionType.MovePlate,
  EActionType.ImagePlate,
  EActionType.OverviewPlate,
  //EActionType.ReserverModules,
  //EActionType.ReleaseModules,
  //EActionType.Scan,
  //EActionType.ChangeTemperature,
  //EActionType.EjectLoadport,
  //EActionType.RetractLoadport,
  //EActionType.Diagnose,
  //EActionType.Other,
];

function getChartOptions(xAxisLabel: string, yAxisLabel: string, titles: string[]) {
  const chartOptions: ChartOptions = {
    title: {
      display: true,
      position: "top",
      fontSize: 18,
      fontFamily: "Arial",
      fontStyle: "bold",
      fontColor: "black",
      text: titles
    },
    layout: {
      padding: {
        bottom: 0,
        top: 0,
      },
    },
    scales: {
      xAxes: [
        {
          stacked: true,
          ticks: {
            fontStyle: "bold",
          },
          gridLines: {
            drawOnChartArea: false,
            drawTicks: true,
            offsetGridLines: false,
            lineWidth: 1,
            color: "black",
          },
          scaleLabel: {
            labelString: xAxisLabel,
            display: xAxisLabel ? true : false,
          },
        },
      ],
      yAxes: [
        {
          stacked: true,
          gridLines: {
            drawOnChartArea: false,
            lineWidth: 1,
            color: "black",
          },
          scaleLabel: {
            labelString: yAxisLabel,
            display: yAxisLabel ? true : false,
            fontStyle: "bold",
            fontSize: 14,
            // offset: 15,
          },
        },
      ],
    },
    plugins: {
      datalabels: {
        align: "start",
        anchor: "end",
        offset: 20,
        display: true,
        color: "black",
        font: {
          weight: "bold",
        },
        formatter: function(value) {
          return Math.round((value + Number.EPSILON) * 1000) / 1000;
        }
      },
    },
    responsive: true,
    legend: {
      display: true,
      align: "start",
      position: "bottom",
      labels: {
        fontColor: "#91929b",
        padding: 20,
        boxWidth: 20,
      },
    },
  };

  return chartOptions;
}

interface EventLogData extends chartjs.ChartData {}

const getChartData = (data: EventLog[], groupBy: string): EventLogData => {
  const result: {
    [index: string]: {
      data: { [actionType: number]: number };
    };
  } = {};

  for (const eventLog of data) {
    const temp: any = eventLog as any;
    const label = temp[groupBy] ? temp[groupBy] : `Unknown ${groupBy}`;
    let curentValue = 0;
    if (result[label]) {
      if (result[label].data[eventLog.actionType]) {
        curentValue = result[label].data[eventLog.actionType] + eventLog.durationInMinute;
      } else {
        result[label].data[eventLog.actionType] = eventLog.durationInMinute;
      }
    } else {
      result[label] = {
        data: {},
      };
      result[label].data[eventLog.actionType] = eventLog.durationInMinute;
    }
    
  }
  const labels: string[] = Object.keys(result);

  let datasets: chartjs.ChartDataSets[] = [];

  for (let i = 0; i < actionTypes.length; i++) {
    const actionType = actionTypes[i];

    const dataset: chartjs.ChartDataSets = {
      label: EActionType[actionType],
      data: labels.map((label) => result[label].data[actionType] / 60),
      stack: "stack",
      backgroundColor: colourPallete[i % colourPallete.length],
      borderWidth: 0,
      maxBarThickness: 167,
    };

    datasets.push(dataset);
  }

  return {
    labels: labels,
    datasets: datasets,
  };
};

type Props = {
  data: EventLog[];
  groupBy: string;
  xAxisLabel: string;
  yAxisLabel: string;
  width: number;
  height: number;
  title: string[];
  setOnClickSave: any;
};

export default function VerticalStackedBarChart({
  data,
  groupBy,
  xAxisLabel,
  yAxisLabel,
  width,
  height,
  title,
  setOnClickSave,
}: Props) {
  const chartRef = useRef(null);
  const chartLegendContainer = useRef(null);
  const chartData: EventLogData = useMemo(() => getChartData(data, groupBy), [data, groupBy]);
  const chartOptions = useMemo(() => getChartOptions(xAxisLabel, yAxisLabel, title), [
    xAxisLabel,
    yAxisLabel,
    title,
  ]);

  useEffect(() => {
    const onSave = () => {
      if(chartRef.current != null && chartRef.current !== undefined){
        const barChart = chartRef.current? chartRef.current : {};
        let chartCanvas = (barChart as Bar).chartInstance.canvas;
        if(chartCanvas){
          const width = chartCanvas.clientWidth;
          const height = chartCanvas.clientHeight;

          let ctx = chartCanvas.getContext("2d");
          if(ctx){
            const cacheCompositionOperation = ctx.globalCompositeOperation;
            ctx.globalCompositeOperation = "destination-over";
            ctx.fillStyle = "white";
            ctx.fillRect(0,0,width,height);
            ctx.globalCompositeOperation = cacheCompositionOperation;
          }
          
          let link = document.createElement('a');
          link.download = 'Usage Chart.jpg';
          link.href = chartCanvas.toDataURL("image/jpeg", 1.0);
          console.log(link.href);
          link.click();
        }
      }
    }

    setOnClickSave({onSave: onSave});
    return () =>
      setOnClickSave({
        onSave: () => {},
      });
  }, [])

  return (
    <div
      style={{
        width: width,
        height: height,
      }}
    >
      <Bar ref={chartRef} data={chartData} options={chartOptions} width={width} height={height}/>
      <div ref={chartLegendContainer}></div>
    </div>
  );
}
